'use client'

import {
    forwardRef,
    useEffect,
    useMemo,
    useState,
    type ButtonHTMLAttributes
} from 'react'
import {Search, SlidersHorizontal, X} from 'lucide-react'
import {SectionAccordion} from '@fitro/customer-ui/SectionAccordion'
import {SelectionDropdown} from '../../ui/Dropdown/Dropdown'
import {SearchInput} from '../../ui/SearchInput/SearchInput'
import {Avatar} from '../../ui/Avatar'
import {ChevronDownIcon} from '../../ui/icons'
import {CalendarPeriodPicker} from '../../ui/CalendarPeriodPicker'
import {cn} from '../../../lib/utils'
import {CLIENT_RWD_PRESS_CLASS} from '../shared/clientRwd'
import {calendarDateTimeForLocale} from '@fitro/date-time/display'
import {useTranslation} from '../../../hooks/useTranslation'
import type {
    CustomerCalendarInstructor,
    CustomerCalendarLocation,
    CustomerCalendarView
} from '../../../services/customer-calendar.service'
import styles from './CustomerCalendarToolbar.module.css'
import {UNASSIGNED_RESOURCE_ID} from '../../../lib/calendar-resource-filter'

interface CustomerCalendarToolbarProps {
    availableClassTypes?: string[]
    classTypes?: string[]
    onClassTypesChange?: (types: string[]) => void
    isDesktop: boolean
    view: CustomerCalendarView
    title: string
    anchor: Date
    today: Date | null
    locations: CustomerCalendarLocation[]
    instructors: CustomerCalendarInstructor[]
    hasUnassignedLocation: boolean
    hasUnassignedInstructor: boolean
    locationIds: string[]
    instructorIds: string[]
    searchQuery: string
    onSearchQueryChange: (value: string) => void
    onViewChange: (view: CustomerCalendarView) => void
    onAnchorChange: (date: Date) => void
    onLocationIdsChange: (ids: string[]) => void
    onInstructorIdsChange: (ids: string[]) => void
    onPrevious: () => void
    onNext: () => void
    onToday: () => void
}

interface FilterOption {
    id: string
    label: string
    avatar?: {name: string; src: string | null}
}

interface FilterTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    label: string
    summary: string
    active: boolean
    avatar?: FilterOption['avatar']
}

interface MultiSelectOptionsProps {
    noneSelectedLabel?: string
    selectedCountLabel?: (count: number) => string
    label: string
    allLabel: string
    emptyLabel: string
    unavailableLabel: string
    options: FilterOption[]
    selectedIds: string[]
    onChange: (ids: string[]) => void
}

interface MultiSelectDropdownProps extends MultiSelectOptionsProps {
    triggerClassName?: string
}

const instructorName = (instructor: CustomerCalendarInstructor) =>
    [instructor.firstName, instructor.lastName].filter(Boolean).join(' ')

const resolveOptions = (
    options: FilterOption[],
    selectedIds: string[],
    unavailableLabel: string
): FilterOption[] => {
    const knownIds = new Set(options.map(option => option.id))
    const resolved = [
        ...selectedIds
            .filter(id => !knownIds.has(id))
            .map(id => ({id, label: unavailableLabel})),
        ...options
    ]
    return [
        ...resolved.filter(option => option.id !== UNASSIGNED_RESOURCE_ID),
        ...resolved.filter(option => option.id === UNASSIGNED_RESOURCE_ID)
    ]
}

const allOptionsSelected = (options: FilterOption[], ids: string[]) => options.length > 0 && options.length === ids.length && options.every(option => ids.includes(option.id))
const explicitFilterSummary = (props: MultiSelectOptionsProps) => allOptionsSelected(props.options, props.selectedIds) ? props.allLabel
    : props.selectedIds.length === 0 ? props.noneSelectedLabel ?? 'None selected'
    : props.selectedIds.length === 1 ? resolveOptions(props.options, props.selectedIds, props.unavailableLabel).find(option => option.id === props.selectedIds[0])?.label ?? props.unavailableLabel
    : props.selectedCountLabel?.(props.selectedIds.length) ?? String(props.selectedIds.length)

const SelectionMark = ({checked, mixed = false}: {checked: boolean; mixed?: boolean}) => (
    <span
        aria-hidden="true"
        className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold',
            checked
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-gray-300 bg-white text-transparent dark:border-gray-600 dark:bg-gray-900'
        )}
    >
        {mixed ? '−' : '✓'}
    </span>
)

const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
    ({label, summary, active, avatar, className, ...buttonProps}, ref) => (
        <button
            {...buttonProps}
            ref={ref}
            type="button"
            className={cn(
                'flex h-11 min-h-11 min-w-0 items-center justify-between gap-3 rounded-xl border bg-white px-3 py-0 text-left shadow-sm transition-colors hover:border-primary-300 focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:bg-gray-900 dark:hover:border-primary-700',
                active
                    ? 'border-primary-300 dark:border-primary-700'
                    : 'border-gray-200 dark:border-gray-700',
                className
            )}
        >
            {avatar ? <span aria-hidden="true" className="shrink-0"><Avatar name={avatar.name} src={avatar.src} size="small" className="size-7" /></span> : null}
            <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-medium leading-4 text-gray-500 dark:text-gray-400">
                    {label}
                </span>
                <span className="block truncate text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">
                    {summary}
                </span>
            </span>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500"/>
        </button>
    )
)
FilterTrigger.displayName = 'FilterTrigger'

const MultiSelectOptions = ({
    label,
    allLabel,
    emptyLabel,
    unavailableLabel,
    options,
    selectedIds,
    onChange
}: MultiSelectOptionsProps) => {
    const allSelected = allOptionsSelected(options, selectedIds)
    const mixed = selectedIds.length > 0 && !allSelected
    const resolvedOptions = useMemo(
        () => resolveOptions(options, selectedIds, unavailableLabel),
        [options, selectedIds, unavailableLabel]
    )

    return (
        <>
            <div className="min-h-0 max-h-64 overflow-y-auto py-1" role="group" aria-label={label}>
                <button
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={mixed ? 'mixed' : allSelected}
                    onClick={() => onChange(!allSelected ? options.map(option => option.id) : [])}
                    className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <SelectionMark checked={allSelected || mixed} mixed={mixed}/>
                    <span className="truncate font-medium">{allLabel}</span>
                </button>
                {resolvedOptions.map(option => {
                    const checked = selectedIds.includes(option.id)
                    return (
                        <button
                            key={option.id}
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={checked}
                            onClick={() => onChange(
                                checked
                                    ? selectedIds.filter(id => id !== option.id)
                                    : [...selectedIds, option.id]
                            )}
                            className={cn('flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800', option.id === UNASSIGNED_RESOURCE_ID && 'border-t border-gray-100 dark:border-gray-700')}
                        >
                            <SelectionMark checked={checked}/>
                            {option.avatar ? <span aria-hidden="true" className="shrink-0"><Avatar name={option.avatar.name} src={option.avatar.src} size="small" className="size-7" /></span> : null}
                            <span className="truncate">{option.label}</span>
                        </button>
                    )
                })}
                {resolvedOptions.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-500">
                        {emptyLabel}
                    </div>
                ) : null}
            </div>
        </>
    )
}

export const MultiSelectDropdown = ({
    triggerClassName,
    ...props
}: MultiSelectDropdownProps) => {
    const {t} = useTranslation()
    return <SelectionDropdown
        modal
        contentAriaLabel={props.label}
        cancelText={t('common.close', 'Zamknij')}
        placement="bottom-start"
        matchTriggerWidth
        className={cn('flex max-h-[360px] flex-col !max-w-[360px] overflow-hidden !p-0', props.options.some(option => option.avatar) && 'min-w-[260px]')}
        trigger={(
            <FilterTrigger
                label={props.label}
                summary={explicitFilterSummary(props)}
                active={!allOptionsSelected(props.options, props.selectedIds)}
                avatar={props.selectedIds.length === 1 ? props.options.find(option => option.id === props.selectedIds[0])?.avatar : undefined}
                className={triggerClassName}
            />
        )}
    >
        <MultiSelectOptions {...props}/>
    </SelectionDropdown>
}

export const CustomerCalendarToolbar = ({
    availableClassTypes = [],
    classTypes = [],
    onClassTypesChange,
    isDesktop,
    view,
    title,
    anchor,
    today: businessToday,
    locations,
    instructors,
    locationIds,
    instructorIds,
    hasUnassignedLocation,
    hasUnassignedInstructor,
    searchQuery,
    onSearchQueryChange,
    onViewChange,
    onAnchorChange,
    onLocationIdsChange,
    onInstructorIdsChange,
    onPrevious,
    onNext,
    onToday
}: CustomerCalendarToolbarProps) => {
    const {t, locale} = useTranslation()
    const dateTime = useMemo(() => calendarDateTimeForLocale(locale), [locale])
    // An active query (e.g. from a shared link) keeps the compact search visible.
    const [mobileSearchOpen, setMobileSearchOpen] = useState(Boolean(searchQuery))
    useEffect(() => {
        if (searchQuery) setMobileSearchOpen(true)
    }, [searchQuery])
    const [instructorFilterExpanded, setInstructorFilterExpanded] = useState(false)
    const [classTypeFilterExpanded, setClassTypeFilterExpanded] = useState(false)

    const viewOptions: CustomerCalendarView[] = isDesktop
        ? ['week', 'month']
        : ['day', 'week', 'month']
    const locationOptions: FilterOption[] = [
        ...locations.map(location => ({id: location.id, label: location.name})),
        ...(hasUnassignedLocation || locationIds.includes(UNASSIGNED_RESOURCE_ID)
            ? [{id: UNASSIGNED_RESOURCE_ID, label: t('client.calendar.filters.withoutLocation', 'Bez lokalizacji')}] : [])
    ]
    const instructorOptions: FilterOption[] = [...instructors.map(instructor => ({
        id: instructor.id,
        label: instructorName(instructor)
            || t('client.calendar.instructorFallback', 'Prowadzący'),
        avatar: {name: instructorName(instructor), src: instructor.avatarUrl}
    })), ...(hasUnassignedInstructor || instructorIds.includes(UNASSIGNED_RESOURCE_ID)
        ? [{id: UNASSIGNED_RESOURCE_ID, label: t('client.calendar.filters.withoutInstructor', 'Bez prowadzącego')}] : [])]
    const viewLabel: Record<CustomerCalendarView, string> = {
        day: t('client.calendar.views.day', 'Dzień'),
        week: t('client.calendar.views.week', 'Tydzień'),
        month: t('client.calendar.views.month', 'Miesiąc')
    }
    const locationFilterProps: MultiSelectOptionsProps = {
        noneSelectedLabel: t('client.calendar.filters.noneSelected', 'Brak wybranych'),
        selectedCountLabel: count => t('client.calendar.filters.selectedTypesCount', 'Wybrano: {count}').replace('{count}', String(count)),
        label: t('client.calendar.filters.location', 'Lokalizacja'),
        allLabel: t('client.calendar.filters.allLocations', 'Wszystkie'),
        emptyLabel: t('client.calendar.filters.noLocations', 'Brak lokalizacji'),
        unavailableLabel: t(
            'client.calendar.filters.unavailableLocation',
            'Niedostępna lokalizacja'
        ),
        options: locationOptions,
        selectedIds: locationIds,
        onChange: onLocationIdsChange
    }
    const instructorFilterProps: MultiSelectOptionsProps = {
        noneSelectedLabel: t('client.calendar.filters.noneSelected', 'Brak wybranych'),
        selectedCountLabel: count => t('client.calendar.filters.selectedTypesCount', 'Wybrano: {count}').replace('{count}', String(count)),
        label: t('client.calendar.filters.instructor', 'Prowadzący'),
        allLabel: t('client.calendar.filters.allInstructors', 'Wszyscy prowadzący'),
        emptyLabel: t('client.calendar.filters.noInstructors', 'Brak prowadzących'),
        unavailableLabel: t(
            'client.calendar.filters.unavailableInstructor',
            'Niedostępny prowadzący'
        ),
        options: instructorOptions,
        selectedIds: instructorIds,
        onChange: onInstructorIdsChange
    }
    const hiddenFilterCount = Number(instructorIds.length > 0)

    const classTypeFilterProps: MultiSelectOptionsProps = {
        noneSelectedLabel: t('client.calendar.filters.noneSelected', 'Brak wybranych'),
        selectedCountLabel: count => t('client.calendar.filters.selectedTypesCount', 'Wybrano: {count}').replace('{count}', String(count)),
        label: t('client.calendar.filters.classTypes', 'Typ zajęć'),
        allLabel: t('client.calendar.filters.allClassTypes', 'Wszystkie'),
        emptyLabel: '',
        unavailableLabel: '',
        options: [
            {id: 'recurring', label: t('client.calendar.filters.recurring', 'Cykliczne')},
            {id: 'event', label: t('client.calendar.filters.events', 'Wydarzenia')},
            {id: 'course', label: t('client.calendar.filters.courses', 'Kursy')},
        ].filter(option => availableClassTypes.includes(option.id)),
        selectedIds: classTypes,
        onChange: types => onClassTypesChange?.(types),
    }
    const hasClassTypeFilter = availableClassTypes.length > 1 && Boolean(onClassTypesChange)
    const compactClassTypeFilter = hasClassTypeFilter ? (
        <div className={styles.compactClassTypeFilter}><SectionAccordion
            variant="filter"
            title={classTypeFilterProps.label}
            summary={explicitFilterSummary(classTypeFilterProps)}
            isOpen={classTypeFilterExpanded}
            onToggle={() => setClassTypeFilterExpanded(value => !value)}

        >
            <MultiSelectOptions {...classTypeFilterProps}/>
        </SectionAccordion></div>
    ) : null
    const closeMobileSearch = () => {
        onSearchQueryChange('')
        setMobileSearchOpen(false)
    }

    return (
        <div
            data-testid="customer-calendar-toolbar"
            data-layout={isDesktop ? 'desktop' : 'compact'}
            className={styles.toolbar}
        >
            <div className={styles.toolbarRow}>
                <CalendarPeriodPicker
                    data-testid="customer-calendar-period-controls"
                    className={styles.periodControls}
                    frameClassName={styles.periodFrame}
                    view={view}
                    views={viewOptions}
                    onViewChange={onViewChange}
                    anchor={anchor}
                    onAnchorChange={onAnchorChange}
                    title={title}
                    previous={{onClick: onPrevious}}
                    next={{onClick: onNext}}
                    onToday={onToday}
                    today={businessToday}
                    dateTime={dateTime}
                    labels={{
                        selectPeriod: t('client.calendar.selectPeriod', 'Wybierz okres'),
                        close: t('common.close', 'Zamknij'),
                        viewSwitcher: t('client.calendar.views.label', 'Widok kalendarza'),
                        views: viewLabel,
                        previousPeriod: t('client.calendar.previous', 'Poprzedni okres'),
                        nextPeriod: t('client.calendar.next', 'Następny okres'),
                        previousMonth: t('client.calendar.previous', 'Poprzedni okres'),
                        nextMonth: t('client.calendar.next', 'Następny okres'),
                        today: t('client.calendar.today', 'Dzisiaj')
                    }}
                />

                <div
                    className={styles.primaryFilters}
                    data-testid="customer-calendar-primary-filters"
                >
                    <SearchInput
                        value={searchQuery}
                        onChange={onSearchQueryChange}
                        placeholder={t('client.calendar.search', 'Szukaj zajęć')}
                        className={styles.desktopSearch}
                        inputClassName="!h-11 !min-h-11 rounded-xl shadow-sm"
                    />
                    {mobileSearchOpen ? (
                        <div
                            className={cn(
                                'min-w-0 flex-1 items-center gap-2',
                                styles.mobileSearch
                            )}
                        >
                            <SearchInput
                                value={searchQuery}
                                onChange={onSearchQueryChange}
                                placeholder={t('client.calendar.search', 'Szukaj zajęć')}
                                autoFocus
                                className="min-w-0 flex-1"
                                inputClassName="!h-11 !min-h-11 rounded-xl shadow-sm"
                            />
                            <button
                                type="button"
                                onClick={closeMobileSearch}
                                aria-label={t(
                                    'client.calendar.closeSearch',
                                    'Zamknij wyszukiwanie'
                                )}
                                className={cn(
                                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:border-primary-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
                                    CLIENT_RWD_PRESS_CLASS
                                )}
                            >
                                <X className="h-[18px] w-[18px]"/>
                            </button>
                        </div>
                    ) : null}
                    {!mobileSearchOpen ? (
                        <button
                            type="button"
                            aria-label={t('client.calendar.search', 'Szukaj zajęć')}
                            onClick={() => setMobileSearchOpen(true)}
                            className={cn(
                                'h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:border-primary-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
                                styles.mobileSearchToggle,
                                CLIENT_RWD_PRESS_CLASS
                            )}
                        >
                            <Search className="h-[18px] w-[18px]"/>
                        </button>
                    ) : null}
                    <MultiSelectDropdown
                        {...locationFilterProps}
                        triggerClassName={cn(
                            styles.locationFilter,
                            mobileSearchOpen && styles.hideWhileMobileSearchOpen
                        )}
                    />
                    {hasClassTypeFilter && <MultiSelectDropdown {...classTypeFilterProps} triggerClassName={styles.classTypeFilter}/>}
                    {(instructorOptions.length > 0 || instructorIds.length > 0) && <MultiSelectDropdown
                        {...instructorFilterProps}
                        triggerClassName={styles.wideInstructorFilter}
                    />}
                    {((instructorOptions.length > 0 || instructorIds.length > 0) || hasClassTypeFilter) && <SelectionDropdown
                        modal
                        cancelText={t('common.close', 'Zamknij')}
                        placement="bottom-end"
                        contentRole="dialog"
                        contentAriaLabel={t('client.calendar.filters.label', 'Filtry')}
                        className="w-[min(380px,calc(100vw-16px))] space-y-2 overflow-y-auto !p-2"
                        trigger={(
                            <button
                                type="button"
                                aria-label={t(
                                    'client.calendar.filters.label',
                                    'Filtry'
                                )}
                                className={cn(
                                    'relative h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:border-primary-300 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
                                    styles.additionalFilters,
                                    CLIENT_RWD_PRESS_CLASS,
                                    mobileSearchOpen && styles.hideWhileMobileSearchOpen
                                )}
                            >
                                <SlidersHorizontal className="h-[18px] w-[18px]"/>
                                {hiddenFilterCount > 0 ? (
                                    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-[11px] font-bold leading-none text-white">
                                        {hiddenFilterCount}
                                    </span>
                                ) : null}
                            </button>
                        )}
                    >
                        {(instructorOptions.length > 0 || instructorIds.length > 0) && (
                            <SectionAccordion
                                variant="filter"
                                title={instructorFilterProps.label}
                                summary={explicitFilterSummary(instructorFilterProps)}
                                isOpen={instructorFilterExpanded}
                                onToggle={() => setInstructorFilterExpanded(value => !value)}
                            >
                                <MultiSelectOptions {...instructorFilterProps}/>
                            </SectionAccordion>
                        )}
                        {compactClassTypeFilter}
                    </SelectionDropdown>}
                </div>
            </div>
        </div>
    )
}
